import { Injectable, OnInit } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { first, map, take, tap } from 'rxjs/operators';

import { RestrictIfService } from 'src/app/auth-o3/shared/restrict/restrict-if.service';
import { ApiService } from 'src/app/auth-o3/shared/services/api.service';
import { AuthService } from 'src/app/auth-o3/shared/services/auth.service';
import { LifecycleEventStatus } from 'src/app/lifecycle-events/models/lifecycle-event-status.enum';
import { STATIC_DATA_TYPE } from 'src/app/static-data/models/consts/static-data.enum';
import { StaticDataArrayType } from 'src/app/static-data/models/interfaces/static-data';
import { FinancialCalendarService } from 'src/app/static-data/services/financial-calendar-static-data.service';
import { StaticDataService } from 'src/app/static-data/services/static-data.service';
import { environment } from 'src/environments/environment';
import { KeyDate } from '../create-product/product-summary/key-dates-modal/key-dates-type.const';
import { ProductSearchFilter } from '../model/classes/product-search-filter';
import { ROLL_CONVENTION_ENUM } from '../model/enums/roll-convention.enum';
import { UNDERLYING_ASSET_TYPE } from '../model/enums/underlying-asset-type.enum';
import { Currency } from '../model/interfaces/currency.interface';
import { IssuerEntity } from '../model/interfaces/issuer-entity.interface';
import { PayoffScheduler } from '../model/interfaces/payoff-scheduler.interface';
import {
  ProductCaptureData,
  ProductCapturePagedData,
  ProductCaptureRow,
} from '../model/interfaces/product-capture.interface';
import {
  CalendarType,
  FinancialCalendar,
} from '../model/interfaces/trading-calendar.interface';
import { UnderlyingAsset } from '../model/interfaces/underlying-asset.interface';
import { sortCurrenciesByG7Order } from '../utils/sortCurrenciesByG7Order';
import { PRODUCT_CAPTURE_ENDPOINTS } from './endpoints/product-capture-endpoints.const';

const HOSTNAME_SCHEDULE = environment.api.schedule;
const HOSTNAME_PRODUCT_CAPTURE = environment.api.productCapture;
const HOSTNAME_STATIC_DATA = environment.api.staticData;
const HOSTNAME_WORKFLOW = environment.api.workflow;

@Injectable({
  providedIn: 'root',
})
export class ProductCaptureService implements OnInit {
  private currencies: Currency[] = [];

  constructor(
    private apiService: ApiService,
    private staticDataService: StaticDataService,
    private authService: AuthService,
    private restrictIfService: RestrictIfService
  ) {}

  ngOnInit(): void {
    this.getCurrencies().subscribe(
      (currencies) => (this.currencies = currencies)
    );
  }

  createProduct(product: ProductCaptureData): Observable<number> {
    return this.apiService.post(
      PRODUCT_CAPTURE_ENDPOINTS.PRODUCT,
      product,
      HOSTNAME_PRODUCT_CAPTURE
    );
  }

  deleteProduct(id: number): Observable<void> {
    return this.apiService.deleteNoBody(
      PRODUCT_CAPTURE_ENDPOINTS.PRODUCT + id,
      HOSTNAME_PRODUCT_CAPTURE
    );
  }

  getProduct(id: number): Observable<ProductCaptureData> {
    return this.apiService.get(
      PRODUCT_CAPTURE_ENDPOINTS.PRODUCT + id,
      HOSTNAME_PRODUCT_CAPTURE
    );
  }

  getProducts(
    searchFilter: ProductSearchFilter
  ): Observable<ProductCapturePagedData> {
    return this.apiService
      .getWithParams<ProductCapturePagedData>(
        PRODUCT_CAPTURE_ENDPOINTS.PRODUCTS,
        { ...searchFilter },
        HOSTNAME_PRODUCT_CAPTURE
      )
      .pipe(first());
  }

  getProductsPagedAndFiltered(
    page: number,
    limit: number,
    searchFilter: ProductSearchFilter = new ProductSearchFilter()
  ): Observable<ProductCapturePagedData> {
    return this.apiService.getWithParams(
      `${PRODUCT_CAPTURE_ENDPOINTS.PRODUCTS}`,
      {
        page,
        limit,
        ...searchFilter,
      },
      HOSTNAME_PRODUCT_CAPTURE
    );
  }

  changeProductLifecycleStatus(
    row: ProductCaptureRow,
    reason: string,
    toState: LifecycleEventStatus
  ): Observable<void> {
    const { productId, lifecycleStatus } = row;

    const endpoint = toState === LifecycleEventStatus.TERMINATED
      ? PRODUCT_CAPTURE_ENDPOINTS.TERMINATE
      : PRODUCT_CAPTURE_ENDPOINTS.CHANGE_LIFECYCLE_STATUS;

    return this.apiService.post(
      `${endpoint}/${productId}`,
      {
        fromState: lifecycleStatus,
        toState,
        reason,
      },
      HOSTNAME_PRODUCT_CAPTURE
    );
  }

  checkForExistingIsin(isin: string): Observable<any> {
    return this.apiService
      .getWithParams(
        `${PRODUCT_CAPTURE_ENDPOINTS.PRODUCTS}/${PRODUCT_CAPTURE_ENDPOINTS.IS_PRODUCT_ISIN_AVAILABLE}`,
        {
          isin,
        },
        HOSTNAME_PRODUCT_CAPTURE
      )
      .pipe(take(1));
  }

  updateProduct(product: ProductCaptureData): Observable<void> {
    return this.apiService.put(
      PRODUCT_CAPTURE_ENDPOINTS.PRODUCT + product.id,
      product,
      HOSTNAME_PRODUCT_CAPTURE
    );
  }

  getCurrencies(): Observable<Currency[]> {
    if (this.currencies.length) {
      return of(this.currencies).pipe(take(1));
    }
    return this.apiService
      .get(PRODUCT_CAPTURE_ENDPOINTS.CURRENCY, HOSTNAME_PRODUCT_CAPTURE)
      .pipe(
        tap((currencies: Currency[]) => {
          this.currencies = sortCurrenciesByG7Order(currencies);
        }),

        map((currencies) => currencies)
      );
  }

  getProductReferenceRatesOptions(): Observable<UnderlyingAsset[]> {
    return this.apiService
      .get(PRODUCT_CAPTURE_ENDPOINTS.UNDERLYING_ASSETS, HOSTNAME_STATIC_DATA)
      .pipe(
        map((response: UnderlyingAsset[]) =>
          response.filter((asset) => asset.type === UNDERLYING_ASSET_TYPE.RATE)
        )
      );
  }

  getUnderlyingAssets(): Observable<UnderlyingAsset[]> {
    return this.apiService.get(
      PRODUCT_CAPTURE_ENDPOINTS.UNDERLYING_ASSETS,
      HOSTNAME_STATIC_DATA
    );
  }

  getReferenceEntities(): Observable<StaticDataArrayType> {
    return this.staticDataService
      .getData(STATIC_DATA_TYPE.REFERENCE_ENTITY)
      .pipe(take(1));
  }

  getReferenceObligations(): Observable<StaticDataArrayType> {
    return this.staticDataService.getData(
      STATIC_DATA_TYPE.REFERENCE_OBLIGATION
    );
  }

  getSchedules(payload: any): Observable<PayoffScheduler> {
    return this.apiService.post(HOSTNAME_SCHEDULE + 'getschedule', payload, '');
  }

  getIssuers(): Observable<IssuerEntity[]> {
    return this.staticDataService.getData(
      STATIC_DATA_TYPE.ISSUERS_ENTITY
    ) as Observable<IssuerEntity[]>;
  }

  setSettlementCalendar(
    productCurrency: string,
    settlementCalendars: string[]
  ): string[] {
    let newCalendar: string[] = [];

    if (productCurrency) {
      const currency = this.currencies.find(
        (c) => c.shortName === productCurrency
      );

      currency?.calendars?.forEach((calendar) => {
        newCalendar.push(calendar);
      });
    }

    newCalendar = this.getUpdatedSettlementCalendarArray(
      settlementCalendars,
      newCalendar
    );

    return newCalendar;
  }

  private getUpdatedSettlementCalendarArray(
    calendars: string[],
    newCalendar: string[]
  ): string[] {
    calendars?.forEach((calendar) => {
      if (!newCalendar.includes(calendar)) {
        newCalendar.push(calendar);
      }
    });
    return newCalendar;
  }

  postToScheduleToResolveKeyDates(keyDatesData: any): Observable<any> {
    return this.apiService.post(
      PRODUCT_CAPTURE_ENDPOINTS.KEY_DATES,
      keyDatesData,
      HOSTNAME_SCHEDULE
    );
  }

  getAllUnderlingAssetCalendars(
    underlyingAssets: UnderlyingAsset[]
  ): string[] | [] {
    if (underlyingAssets) {
      const calendars = underlyingAssets.map((obj) => obj.calendars);
      const calendarsFlat = calendars.reduce(
        (acc, curr) => acc.concat(curr),
        []
      );
      const calendarsUnique = [...new Set(calendarsFlat)];
      return calendarsUnique;
    }
    return [];
  }

  updateCalenderItemDisabledStatus(
    calendarOptions: KeyDate[] = [],
    calendarCodes: string[] = []
  ): KeyDate[] {
    const copyOfCalendarOptions = JSON.parse(JSON.stringify(calendarOptions));
    return copyOfCalendarOptions?.map((obj) => {
      if (calendarCodes?.includes(obj.id)) {
        obj.disabled = true;
      } else {
        obj.disabled = false;
      }
      return obj;
    });
  }

  getCalendarsAsDropdownOptions(type: CalendarType): Observable<KeyDate[]> {
    return this.staticDataService
      .getService(FinancialCalendarService)
      .getAllOfType(type, true)
      .pipe(
        map((data: FinancialCalendar[]) =>
          data.map((calendar: FinancialCalendar) => ({
            id: calendar.calendarCode,
            displayValue: calendar.calendarCode,
            disabled: false,
          }))
        )
      );
  }

  getCurrencyCalendarsFromCurrencyValue(
    currencyValue: string,
    currencies: Currency[]
  ): string[] | [] {
    const curr = currencies.filter((obj) => currencyValue === obj.shortName);
    return curr.length === 1 ? curr[0]?.calendars : [];
  }

  updateSettlementCalendarItems(
    currency: string,
    currenciesArr: Currency[],
    settlementCalendars: KeyDate[]
  ): KeyDate[] {
    const calendarCodes = this.getCurrencyCalendarsFromCurrencyValue(
      currency,
      currenciesArr
    );
    const settlementCalendarItemsUpdated =
      this.updateCalenderItemDisabledStatus(settlementCalendars, calendarCodes);
    return settlementCalendarItemsUpdated;
  }

  canUserEditProduct(product: ProductCaptureData): Observable<boolean> {
    if (!product) {
      return of(false);
    }

    const restriction = this.authService.isProductTraded(product)
      ? 'EDIT_PRODUCTS_WITH_TRADED_STATUS'
      : 'EDIT_EXISTING_PRODUCTS';

    const isDeutscheBankUser = this.authService
      .getUserTenant()
      .pipe(
        map(
          (tenant) =>
            tenant === 'DeutscheBank' && product.summary.productType !== 'CLN'
        )
      );

    return combineLatest([
      this.restrictIfService.restrictTo(restriction),
      isDeutscheBankUser,
    ]).pipe(
      first(),
      map(([hasPermission, isDeutscheBank]) => hasPermission && !isDeutscheBank)
    );
  }

  exportDetails(
    productId: number,
    integratorName?: string
  ): Observable<string> {
    return this.apiService
      .getWithParams(
        `json/${productId}`,
        { ...(integratorName && { integratorName }) },
        environment.api.productData,
        'text'
      )
      .pipe(
        take(1),
        map((response: any) => {
          if (response.startsWith('<?xml')) {
            return response;
          }
          return JSON.stringify(JSON.parse(response), null, '\t');
        }),
        map((response: string) =>
          URL.createObjectURL(
            new Blob([response], { type: 'application/json;charset=utf-8' })
          )
        )
      );
  }

  getSettlementRollConvention(
    productType: string,
    defaultValue?: string
  ): string {
    return productType === 'CLN'
      ? defaultValue || ROLL_CONVENTION_ENUM.MODIFIED_FOLLOWING
      : defaultValue || ROLL_CONVENTION_ENUM.FOLLOWING;
  }

  getProductStatusAudit(productId: string): Observable<any[]> {
    return this.apiService.getWithParams(
      PRODUCT_CAPTURE_ENDPOINTS.STATUS_AUDIT,
      {
        productId,
      },
      HOSTNAME_WORKFLOW
    );
  }
}
